import { useId, useState, type FormEvent } from 'react';
import type { Category } from '../../shared/api';
import { MAX_CATEGORY_NAME_LENGTH } from '../../shared/api';
import { errorMessage } from '../lib/api';

interface NewCategoryFormProps {
  create: (name: string) => Promise<Category>;
  onCreated: (category: Category) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}

/** An inline "name + Add" form for creating one of your own categories. */
export function NewCategoryForm({ create, onCreated, onCancel, autoFocus = false }: NewCategoryFormProps) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const category = await create(name);
      setName('');
      onCreated(category);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="new-category" onSubmit={(event) => void handleSubmit(event)}>
      <label htmlFor={inputId} className="visually-hidden">
        New category name
      </label>
      <div className="new-category__row">
        <input
          id={inputId}
          type="text"
          placeholder="New category, e.g. Upstairs library"
          maxLength={MAX_CATEGORY_NAME_LENGTH}
          autoComplete="off"
          autoFocus={autoFocus}
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-invalid={error !== null}
        />
        <button type="submit" className="btn btn--primary btn--small" disabled={saving || !name.trim()}>
          {saving ? 'Adding…' : 'Add'}
        </button>
        {onCancel && (
          <button type="button" className="btn btn--secondary btn--small" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
